/**
 * Min-heap priority queue over integer keys `0..<tieRank.length` with
 * O(log n) decrease-key via a key→position array. Used by the A* open set,
 * keyed by vertex index. Port of iOS PriorityQueue.swift.
 */
export interface PQItem {
  readonly key: number
  priority: number
}

export class PriorityQueue {
  private heap: PQItem[] = []
  private readonly position: Int32Array
  private readonly tieRank: ArrayLike<number>

  /**
   * `tieRank[key]` orders keys of equal priority — A* passes the rank of each
   * vertex key `id|line`, so equal priorities pop in key order.
   */
  constructor(tieRank: ArrayLike<number>) {
    this.tieRank = tieRank
    this.position = new Int32Array(tieRank.length).fill(-1)
  }

  get isEmpty(): boolean {
    return this.heap.length === 0
  }

  /**
   * Inserts key with priority, or updates it if key already exists
   * and the new priority is lower. Higher-priority updates are ignored.
   */
  pushOrUpdate(key: number, priority: number): void {
    const i = this.position[key]
    if (i >= 0) {
      if (this.precedes(priority, key, this.heap[i].priority, this.heap[i].key)) {
        this.heap[i].priority = priority
        this.siftUp(i)
      }
      return
    }
    this.heap.push({ key, priority })
    this.position[key] = this.heap.length - 1
    this.siftUp(this.heap.length - 1)
  }

  /** Removes and returns the item with the lowest priority. */
  pop(): PQItem | undefined {
    if (this.heap.length === 0) return undefined
    const top = this.heap[0]
    this.position[top.key] = -1
    const last = this.heap.pop()!
    if (this.heap.length > 0) {
      this.heap[0] = last
      this.position[last.key] = 0
      this.siftDown(0)
    }
    return top
  }

  private siftUp(start: number): void {
    let i = start
    while (i > 0) {
      const p = (i - 1) >> 1
      if (this.less(i, p)) {
        this.swap(i, p)
        i = p
      } else {
        break
      }
    }
  }

  private siftDown(start: number): void {
    let i = start
    const n = this.heap.length
    while (true) {
      const l = 2 * i + 1
      const r = 2 * i + 2
      let s = i
      if (l < n && this.less(l, s)) s = l
      if (r < n && this.less(r, s)) s = r
      if (s === i) break
      this.swap(i, s)
      i = s
    }
  }

  private less(a: number, b: number): boolean {
    return this.precedes(
      this.heap[a].priority,
      this.heap[a].key,
      this.heap[b].priority,
      this.heap[b].key,
    )
  }

  private swap(a: number, b: number): void {
    const t = this.heap[a]
    this.heap[a] = this.heap[b]
    this.heap[b] = t
    this.position[this.heap[a].key] = a
    this.position[this.heap[b].key] = b
  }

  /**
   * Pop order: lower priority first, and on equal priority the lower tie rank
   * — so the order never depends on insertion order, which differs with the
   * order a platform walks a vertex's edges. iOS ranks the same keys; Android
   * compares the key strings themselves. That is what keeps offline routes
   * identical across platforms.
   */
  private precedes(
    priority: number,
    key: number,
    otherPriority: number,
    otherKey: number,
  ): boolean {
    return (
      priority < otherPriority ||
      (priority === otherPriority && this.tieRank[key] < this.tieRank[otherKey])
    )
  }
}
